import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, BRL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Star, Loader2, Zap, ShieldCheck } from "lucide-react";

const PLAN_META = {
  vaga_avulsa: { period: "30 dias", features: ["1 vaga publicada", "Ativa por 30 dias", "Sem renovação automática"], icon: Zap },
  vaga_destaque: { period: "30 dias", highlight: true, features: ["1 vaga em destaque", "Aparece no topo da página inicial", "Sem renovação automática"], icon: Star },
  plano_empresa: { period: "30 dias", features: ["Até 5 vagas ativas", "Painel de gestão", "Renovação manual, sem cobrança automática"], icon: Check },
  plano_pro: { period: "30 dias", features: ["Até 20 vagas ativas", "Vagas em destaque incluídas", "Renovação manual, sem cobrança automática"], icon: Star },
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(Array.isArray(r.data) ? r.data : []));
  }, []);

  const buy = async (code) => {
    if (!user) {
      toast.info("Entre com sua empresa para contratar um plano.");
      navigate("/login");
      return;
    }
    if (user.role === "admin") {
      toast.info("Contas de administrador não contratam planos.");
      return;
    }
    if (!accepted) {
      toast.error("Leia e aceite os Termos de Uso e a Política de Cancelamento e Reembolso para continuar.");
      return;
    }
    setLoadingId(code);
    try {
      const res = await api.post("/checkout", {
        plan_code: code,
        terms_accepted: true,
        terms_version: "2026-09-12",
      });
      window.location.assign(res.data.checkout_url || res.data.sandbox_checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível iniciar o pagamento.");
      setLoadingId(null);
    }
  };

  return (
    <div className="bg-slate-50">
      <section className="bg-gradient-to-r from-blue-700 to-indigo-900 text-white grain">
        <div className="max-w-7xl mx-auto px-5 py-16 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">Publique sua vaga</h1>
          <p className="mt-4 text-blue-100 text-lg max-w-2xl mx-auto">
            Divulgue para candidatos da Região Metropolitana de Campinas. Grátis para candidatos, planos acessíveis para empresas.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm font-bold">
            <ShieldCheck size={17} /> 30 dias • renovação manual • sem cobrança recorrente automática
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p) => {
            const meta = PLAN_META[p.code] || {};
            const Icon = meta.icon || Check;
            return (
              <div key={p.code} data-testid={`plan-${p.code}`}
                className={`relative bg-white rounded-3xl p-8 border shadow-[0_8px_32px_rgba(0,0,0,0.04)] flex flex-col ${meta.highlight ? "border-emerald-400 ring-2 ring-emerald-400" : "border-slate-200"}`}>
                {meta.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-400 text-slate-950 text-xs font-extrabold px-3 py-1 rounded-full">Mais visibilidade</span>}
                <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-blue-700 to-indigo-900 text-white grid place-items-center mb-4"><Icon size={20} /></div>
                <h3 className="font-heading text-xl font-extrabold tracking-tight text-slate-950">{p.title}</h3>
                <p className="mt-3">
                  <span className="font-heading text-3xl font-extrabold text-slate-950">{BRL(p.amount)}</span>
                  <span className="text-slate-500 font-semibold"> / {meta.period}</span>
                </p>
                <p className="text-xs font-bold text-slate-500 mt-2">Pagamento único para o período informado.</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {(meta.features || []).map((f) => <li key={f} className="flex items-start gap-2 text-slate-700"><Check size={18} className="text-emerald-500 shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
                <button data-testid={`buy-${p.code}`} onClick={() => buy(p.code)} disabled={loadingId === p.code || !accepted}
                  className="mt-8 w-full bg-emerald-400 text-slate-950 font-extrabold py-3 rounded-xl hover:bg-emerald-500 hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2">
                  {loadingId === p.code ? <Loader2 className="animate-spin" size={18} /> : "Contratar"}
                </button>
              </div>
            );
          })}
        </div>

        <label className="max-w-3xl mx-auto mt-8 flex items-start gap-3 rounded-2xl bg-white border border-slate-200 p-5 cursor-pointer">
          <input data-testid="checkout-terms" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0" />
          <span className="text-sm text-slate-700 leading-relaxed">
            Li e aceito os <Link to="/termos" target="_blank" className="text-blue-700 font-bold hover:underline">Termos de Uso</Link>, a <Link to="/privacidade" target="_blank" className="text-blue-700 font-bold hover:underline">Política de Privacidade</Link> e a <Link to="/reembolso" target="_blank" className="text-blue-700 font-bold hover:underline">Política de Cancelamento e Reembolso</Link>. Entendo que a contratação atual vale por 30 dias e não possui renovação automática.
          </span>
        </label>

        <p className="text-center text-slate-500 mt-8">
          Pagamento processado com segurança pelo Mercado Pago. A plataforma não armazena os dados completos do cartão. As vagas passam por aprovação antes de aparecer no site.
        </p>
      </div>
    </div>
  );
}
